/**
 * ConversationEmptyState — who is in the room, above a centered composer.
 *
 * Shown while a twin conversation has no messages yet (1:1, group, in or out
 * of a study, guest or owner). The room's twins are the home page's cover
 * flow (TwinCoverFlow): up to three cards at once, the active twin in front,
 * arrows and side-card clicks to rotate. The host passes its composer as
 * children so both sit together in the middle of the page. The prompt ("Ask
 * the group anything") lives in the composer's placeholder; here it only names
 * the flow for assistive tech. Once the first message lands the host switches
 * back to its normal thread layout.
 *
 * A twin's saved segments show as tags under its first name on the card,
 * colored by first-seen order across the room (one tag plus "+N"); twins
 * without segments (e.g. a guest 1:1) show none.
 */

import { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { SegmentTags } from '@/components/chat/SegmentTags/SegmentTags'
import { TwinCoverFlow } from '@/components/chat/TwinCoverFlow/TwinCoverFlow'
import { getSegmentColorSlots } from '@/lib/segments/segmentColor'

export interface ConversationEmptyStateTwin {
  id: string
  name: string
  imageUrl?: string | null
  /** The twin's saved segment names, shown as colored tags under its name. */
  segmentNames?: Array<string>
}

interface ConversationEmptyStateProps {
  twins: Array<ConversationEmptyStateTwin>
  /** The empty-room prompt; names the card row (the composer shows it as its placeholder). */
  headline: string
  /** Wraps a twin's card in its profile (hover card on desktop, sheet on phones). */
  renderProfile?: (twinIndex: number, card: ReactElement) => ReactNode
  /** Content under a twin's name (e.g. an insight line). */
  renderBelowName?: (twinIndex: number) => ReactNode
  /** The host's composer, rendered under the cards. */
  children?: ReactNode
}

export function ConversationEmptyState(props: ConversationEmptyStateProps) {
  const { twins, headline, renderProfile, renderBelowName, children } = props

  const { t } = useTranslation('common')

  // One slot per segment for the whole room, in first-seen order, so a segment matches on every card.
  const segmentSlots = getSegmentColorSlots(twins.map((twin) => twin.segmentNames))

  const renderUnderName = (twinIndex: number) => {
    const segmentNames = twins[twinIndex]?.segmentNames ?? []
    const extra = renderBelowName?.(twinIndex)
    if (segmentNames.length === 0 && !extra) {
      return null
    }
    return (
      <div className="flex min-w-0 max-w-full flex-col items-start gap-1.5">
        <SegmentTags
          names={segmentNames}
          colorSlots={segmentSlots}
          tone="onImage"
          data-testid={`twin-poster-segment-tags-${twinIndex}`}
        />
        {extra}
      </div>
    )
  }

  return (
    <div
      className="mx-auto flex w-full max-w-3xl flex-col items-center pb-8"
      data-testid="conversation-empty-state"
    >
      <TwinCoverFlow
        twins={twins}
        label={headline}
        renderCard={renderProfile}
        renderBelowName={renderUnderName}
        previousLabel={t('previous')}
        nextLabel={t('next')}
        className="mb-6"
      />

      {children && <div className="w-full">{children}</div>}
    </div>
  )
}
